import React, { cloneElement, ReactElement } from 'react';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { ymTrackSwitchProductsAutoImport } from '../../utils/metrika';
import {
    excludeCyrillicRegExp,
    excludeSymbolsRegExp,
    instagramProfileRegExp,
    publicIdRegExp,
} from '../../utils/regexps';
import { useTranslation } from '../../utils/useTranslation';
import { composeValidators } from '../../utils/validators/compose';
import { makeEmptyValidator } from '../../utils/validators/makeEmptyValidator';
import { makeMaxLengthValidator } from '../../utils/validators/makeMaxLengthValidator';
import { makeRegExpValidator } from '../../utils/validators/makeRegExpValidator';
import { makeRequiredValidator } from '../../utils/validators/makeRequiredValidator';
import { makeUrlValidator } from '../../utils/validators/makeUrlValidator';
import { Validator } from '../../utils/validators/types';
import { confirmAlert } from '../feedback/Alert';
import { WithHint } from '../feedback/Tooltip';
import { Button } from '../input/Button';
import { FormControl } from '../input/FormControl';
import { FormControlGroup } from '../input/FormControlGroup';
import { FormControlLabel, LabelPlacement } from '../input/FormControlLabel';
import { Input } from '../input/Input';
import { Radio } from '../input/Radio';
import { Switch } from '../input/Switch';
import { TextArea } from '../input/TextArea';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';

import type { BusinessPersistenceUI } from './types';

const DESCRIPTION_MAX_LENGTH = 300;

interface BusinessPersistenceFormProps {
    initialValues: Partial<BusinessPersistenceUI>;
    onSubmit: (formValues: BusinessPersistenceUI) => Promise<void>;
    membersSlot: ReactElement;
    categoriesSlot: ReactElement;
    categoriesChanged: boolean;
    canEditPublicId: boolean;
    canEditInstagramUrl: boolean;
    canEditBusiness: boolean;
    canEditBusinessCategories: boolean;
    canEditProducts?: boolean;
    login?: string;
    publicId?: string;
    onSupportClick?: VoidFunction;
    onFieldBlur: (fieldName: string, value: string) => void;
}

const validateDisplayName = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
    makeMaxLengthValidator(25, 'Название профиля не должно превышать 25 символов'),
);

const validatePublicId = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(
        publicIdRegExp,
        'Имя профиля должно начинаться с символа @ и содержать как минимум один символ',
    ),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
    makeRegExpValidator(excludeCyrillicRegExp, 'Не используйте кириллические символы'),
);

const validateDescription = composeValidators(
    makeEmptyValidator(),
    makeMaxLengthValidator(
        DESCRIPTION_MAX_LENGTH,
        `Название профиля не должно превышать ${DESCRIPTION_MAX_LENGTH} символов`,
    ),
);

const validateSiteDisplay = composeValidators(makeRequiredValidator(), makeUrlValidator());

interface SourceFieldsProps {
    sourceLabel: string;
    urlFieldProps?: {
        urlSourceName?: string;
        urlValue?: string;
        urlPlaceHolder?: string;
        urlValidator?: Validator<string>;
    };
    importFormatPath: string;
    messageSlot: ReactElement;
    disabled: boolean;
    canEditBusiness: boolean;
    onFieldBlur: (fieldName: string, value: string) => void;
}

const ImportSourceFieldset = (props: SourceFieldsProps) => {
    const t = useTranslation();
    const {
        sourceLabel,
        urlFieldProps = {},
        importFormatPath,
        messageSlot,
        disabled,
        canEditBusiness,
        onFieldBlur,
    } = props;
    const { urlSourceName, urlValue, urlPlaceHolder, urlValidator } = urlFieldProps;

    return (
        <>
            <Flex direction="row" py={2} spacing={0.5} alignItems="center">
                <WithHint
                    message={
                        'Используйте ссылки только на профиль вашего бизнеса.\n\nПосты будут загружаться со следующего дня после добавления источника.\n\nПосты содержащие запрещённые слова будут скачиваться в черновики.\n\nЗапрещены любые упоминания сервисов Meta*, розыгрыши, призывы писать в комментарии.'
                    }
                >
                    <Typography variant="body" weight="bold">
                        {sourceLabel}
                    </Typography>
                </WithHint>
            </Flex>
            {urlSourceName && (
                <Field
                    name={urlSourceName}
                    validate={urlValidator}
                    render={({ input, meta }) => (
                        <FormControl
                            pt={1}
                            pb={urlValue ? 1 : 2}
                            label={t('Ссылка на профиль компании')}
                            error={meta.touched && Boolean(meta.error)}
                            message={meta.touched ? meta.error : undefined}
                            disabled={disabled}
                        >
                            <Input
                                {...input}
                                type="text"
                                autoComplete="off"
                                placeholder={urlPlaceHolder}
                            />
                        </FormControl>
                    )}
                />
            )}
            {cloneElement(messageSlot)}
            {urlValue && (
                <Field
                    name={importFormatPath}
                    render={({ input }) => (
                        <FormControlGroup width="100%" {...input}>
                            <FormControlLabel
                                label={t('Скачивать и публиковать')}
                                borderBottom="2px solid var(--divider)"
                                control={
                                    <Radio
                                        value="import_publish"
                                        onChange={(event) => {
                                            input.onChange(event);
                                            onFieldBlur(
                                                'import_publish',
                                                event.currentTarget.checked ? 'checked' : '',
                                            );
                                        }}
                                    />
                                }
                                disabled={!canEditBusiness}
                            />
                            <FormControlLabel
                                label={t('Скачивать и сохранять в черновики')}
                                borderBottom="2px solid var(--divider)"
                                control={
                                    <Radio
                                        value="import_draft"
                                        onChange={(event) => {
                                            input.onChange(event);
                                            onFieldBlur(
                                                'import_draft',
                                                event.currentTarget.checked ? 'checked' : '',
                                            );
                                        }}
                                    />
                                }
                                disabled={!canEditBusiness}
                            />
                            <FormControlLabel
                                label={t('Не скачивать')}
                                control={
                                    <Radio
                                        value="do_not_import"
                                        onChange={(event) => {
                                            input.onChange(event);
                                            onFieldBlur(
                                                'do_not_import',
                                                event.currentTarget.checked ? 'checked' : '',
                                            );
                                        }}
                                    />
                                }
                                disabled={!canEditBusiness}
                            />
                        </FormControlGroup>
                    )}
                />
            )}
        </>
    );
};

export const BusinessPersistenceForm = (props: BusinessPersistenceFormProps) => {
    const {
        initialValues,
        membersSlot,
        categoriesSlot,
        categoriesChanged,
        onSubmit,
        canEditInstagramUrl,
        canEditPublicId,
        canEditBusiness,
        canEditBusinessCategories,
        canEditProducts,
        login,
        publicId,
        onSupportClick,
        onFieldBlur,
    } = props;
    const t = useTranslation();

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            mutators={{
                setUseProductsAutoImport: (args, state, utils) => {
                    utils.changeValue(state, 'useProductsAutoImport', () => false);
                },
            }}
            render={({
                submitting,
                pristine,
                invalid,
                submitError,
                handleSubmit,
                values,
                form,
            }) => (
                <Flex
                    as="form"
                    width="100%"
                    height={{ xs: '100%', sm: 'auto' }}
                    onSubmit={handleSubmit}
                    spacing={1}
                >
                    <Flex
                        px={2}
                        width="100%"
                        height={{ xs: '100%', sm: 'auto' }}
                        borderRadius={2}
                        backgroundColor="var(--background-primary)"
                    >
                        <WithHint
                            message={
                                'Заполните профиль так, чтобы пользователям был понятен и интересен ваш продукт.\n\nОписание не должно содержать нецензурную лексику и жаргоны.'
                            }
                        >
                            <Typography variant="title3" weight="bold" py={2}>
                                {t('Основная информация')}
                            </Typography>
                        </WithHint>

                        <Field
                            name="publicId"
                            validate={validatePublicId}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    required
                                    label={t('Имя профиля')}
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                    disabled={!canEditPublicId}
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder={t('Не заполнено')}
                                    />
                                </FormControl>
                            )}
                        />
                        <Typography variant="caption" color="secondary" pb={2}>
                            {t('Имя профиля можно изменить только через ')}
                            <Typography
                                display="inline"
                                as={Link}
                                to={
                                    process.env.REACT_APP_SUPPORT_FORM_LINK
                                        ? `${process.env.REACT_APP_SUPPORT_FORM_LINK}?login=${login}`
                                        : ''
                                }
                                color="link"
                                textDecoration="none"
                                variant="caption"
                                onClick={onSupportClick}
                            >
                                {t('поддержку')}
                            </Typography>
                        </Typography>

                        <Field
                            name="displayName"
                            validate={validateDisplayName}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    required
                                    disabled={!canEditBusiness}
                                    label={t('Название бизнеса')}
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder={t('Не заполнено')}
                                        onBlur={(event) => {
                                            onFieldBlur('displayName', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <Field
                            name="description"
                            validate={validateDescription}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    label={t('Описание профиля')}
                                    disabled={!canEditBusiness}
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <TextArea
                                        {...input}
                                        rows={4}
                                        autoComplete="off"
                                        placeholder={t('Не заполнено')}
                                        onBlur={(event) => {
                                            onFieldBlur('description', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <Field
                            name="links.siteDisplay"
                            validate={validateSiteDisplay}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    label={t('Ссылка на сайт')}
                                    hint={t(
                                        'Укажите ссылку на ваш сайт или его раздел.\n\nСсылка не должна содержать utm_source, сервис добавит данную  метку самостоятельно.',
                                    )}
                                    error={meta.touched && Boolean(meta.error)}
                                    disabled={!canEditBusiness}
                                    message={meta.touched ? meta.error : undefined}
                                    required
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder="https://"
                                        onBlur={(event) => {
                                            onFieldBlur('links.siteDisplay', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <Field
                            type="checkbox"
                            name="hideInFeed"
                            render={({ input }) => (
                                <FormControlLabel
                                    label={
                                        <WithHint
                                            message={
                                                'При включении этого режима пользователям не будут рекомендоваться ваши посты.\n\nЧерез поиск и по прямой ссылке ваш профиль можно будет найти.'
                                            }
                                        >
                                            {t('Скрыть посты из ленты пользователей')}
                                        </WithHint>
                                    }
                                    disabled={!canEditBusiness}
                                    control={
                                        <Switch
                                            {...input}
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'hideInFeed',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                    labelPlacement={LabelPlacement.LEFT}
                                />
                            )}
                        />
                    </Flex>
                    {canEditBusinessCategories && cloneElement(categoriesSlot)}
                    <Flex
                        width="100%"
                        spacing={0.5}
                        px={2}
                        height={{ xs: '100%', sm: 'auto' }}
                        borderRadius={2}
                        backgroundColor="var(--background-primary)"
                    >
                        <Typography variant="title3" weight="bold" py={2}>
                            {t('Источники контента')}
                        </Typography>

                        <ImportSourceFieldset
                            sourceLabel="Instagram*"
                            disabled={!canEditInstagramUrl}
                            canEditBusiness={canEditBusiness}
                            onFieldBlur={onFieldBlur}
                            urlFieldProps={{
                                urlSourceName: 'instagram.url',
                                urlValue: values.instagram?.url,
                                urlPlaceHolder: 'https://www.instagram.com/username',
                                urlValidator: composeValidators(
                                    makeRequiredValidator(),
                                    makeUrlValidator(),
                                    makeRegExpValidator(
                                        instagramProfileRegExp,
                                        'Укажите ссылку в формате https://www.instagram.com/username',
                                    ),
                                ),
                            }}
                            messageSlot={
                                <Typography variant="caption" color="secondary" pb={2}>
                                    {t('Ссылку на инстаграм можно изменить только через ')}
                                    <Typography
                                        display="inline"
                                        as={Link}
                                        to={
                                            process.env.REACT_APP_SUPPORT_FORM_LINK
                                                ? `${process.env.REACT_APP_SUPPORT_FORM_LINK}?login=${login}`
                                                : ''
                                        }
                                        color="link"
                                        textDecoration="none"
                                        variant="caption"
                                        onClick={onSupportClick}
                                    >
                                        {t('поддержку')}
                                    </Typography>
                                </Typography>
                            }
                            importFormatPath="instagram.importFormat"
                        />
                    </Flex>

                    <Typography px={2} color="secondary" variant="caption">
                        {t(
                            '*Meta, владеющая Instagram, признана экстремистской организацией на территории РФ',
                        )}
                    </Typography>

                    {canEditProducts && (
                        <Flex
                            width="100%"
                            spacing={0.5}
                            px={2}
                            height={{ xs: '100%', sm: 'auto' }}
                            borderRadius={2}
                            backgroundColor="var(--background-primary)"
                        >
                            <Typography variant="title3" weight="bold" py={2}>
                                {t('Товары β')}
                            </Typography>
                            <Field
                                type="checkbox"
                                name="useProductsAutoImport"
                                render={({ input }) => (
                                    <FormControlLabel
                                        label={
                                            <WithHint
                                                message={
                                                    'Мы применяем технологии компьютерного зрения для распознавания товаров. Если получается распознать товар, то на посте появляется мини-превью товара с ссылкой на товар. По этой ссылке пользователь может перейти сразу в товар и приобрести его. Товары находятся на сайте того бизнеса, который создал пост. Для распознавания товаров не используются товары других продавцов.\n\n' +
                                                    'Посмотреть на конкретные посты с товарами можно из профиля бизнеса при переходе в посты.'
                                                }
                                            >
                                                {t('Автоматически добавлять товары к постам')}
                                            </WithHint>
                                        }
                                        disabled={!canEditBusiness}
                                        control={
                                            <Switch
                                                {...input}
                                                onChange={async (event) => {
                                                    ymTrackSwitchProductsAutoImport({
                                                        page: 'Настройки бизнеса',
                                                        userLogin: login,
                                                        businessId: publicId,
                                                    });
                                                    if (event.currentTarget.checked) {
                                                        input.onChange(event);
                                                    } else {
                                                        try {
                                                            await confirmAlert(
                                                                'Отключение автодобавления',
                                                                'При выключении автоматического распознавания товаров все ранее добавленные товары будут удалены.',
                                                                'отмена',
                                                                'всё равно отключить',
                                                            );
                                                            form.mutators.setUseProductsAutoImport();
                                                        } catch (error) {
                                                            return;
                                                        }
                                                    }
                                                }}
                                            />
                                        }
                                        labelPlacement={LabelPlacement.LEFT}
                                    />
                                )}
                            />
                        </Flex>
                    )}
                    {cloneElement(membersSlot)}
                    <Flex width="100%" pb={2} pt={1}>
                        <Box mt="auto" width="100%">
                            {submitError && (
                                <Typography variant="caption" color="error" mb={2}>
                                    {submitError}
                                </Typography>
                            )}
                            <Button
                                width="100%"
                                type="submit"
                                disabled={
                                    (pristine && !categoriesChanged) ||
                                    submitting ||
                                    invalid ||
                                    !canEditBusiness
                                }
                            >
                                {t('Сохранить')}
                            </Button>
                        </Box>
                    </Flex>
                </Flex>
            )}
        />
    );
};
